import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getKintoneIconUrl } from "~/config/kintoneIcons";
import ArrowDropDownClose from "../../../images/arrow_drop_down_close.svg?react";
import ArrowDropDownOpen from "../../../images/arrow_drop_down_open.svg?react";
import type { RootState } from "../../../store";
import styles from "./index.module.scss";

// requirement.ts準拠のTypeScript型定義
interface Requirement {
  id: string;
  name: string; // title → name に変更
  description: string;
  priority: "high" | "medium" | "low"; // level → priority に変更（小文字）
}

interface RequirementCategory {
  name: string;
  requirements: Requirement[];
}

interface FunctionalRequirements {
  processManagement: RequirementCategory;
  accessRights: RequirementCategory;
  notifications: RequirementCategory;
  actions: RequirementCategory;
  viewFunctions: RequirementCategory;
  extensions: RequirementCategory;
  customizations: RequirementCategory;
}

interface AppRequirement {
  appId: string;
  name: string;
  purpose: string; // 新規追加
  fieldCount: number;
  recordCount: number;
  iconFileKey?: string | null;
  functionalRequirements: FunctionalRequirements; // categories → functionalRequirements
}

interface RequirementsAnalysisData {
  systemPurpose: string; // 新規追加
  apps: AppRequirement[];
}

interface RequirementsAnalysisResultProps {
  data?: RequirementsAnalysisData | undefined;
  onExport?: (() => void) | undefined;
  projectName?: string | undefined;
  documentId?: string | undefined;
  isOtherTabEditing?: boolean | undefined;
  onEditingChange?: ((isEditing: boolean) => void) | undefined;
  report?: RootState["reverseEngineering"]["reportData"] | undefined;
}

const RequirementsAnalysisResult: React.FC<RequirementsAnalysisResultProps> = ({ data }) => {
  const { isEditable, reportData } = useSelector((state: RootState) => state.reverseEngineering);
  const isEditMode = isEditable.requirements;

  // Local state for editing
  const [selectedAppId, setSelectedAppId] = useState<string>("");
  const [editableData, setEditableData] = useState<RequirementsAnalysisData | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({
    processManagement: true,
    accessRights: false,
    notifications: false,
    actions: false,
    viewFunctions: false,
    extensions: false,
    customizations: false,
  });
  const [currentIconUrl, setCurrentIconUrl] = useState<string | undefined>();
  const currentIconObjectUrl = useRef<string | null>(null);

  useEffect(() => {
    if (isEditMode) {
      setEditableData((prev) => prev ?? (data ? JSON.parse(JSON.stringify(data)) : null));
    } else {
      setEditableData(null);
    }
  }, [isEditMode, data]);

  // データがない場合は空のデータ構造を使用
  const displayData: RequirementsAnalysisData =
    isEditMode && editableData
      ? editableData
      : data || {
          systemPurpose: "",
          apps: [],
        };

  useEffect(() => {
    if (!displayData.apps.length) {
      setSelectedAppId("");
      return;
    }

    setSelectedAppId((prev) => {
      if (prev && displayData.apps.some((app) => app.appId === prev)) {
        return prev;
      }
      return displayData.apps[0]?.appId ?? "";
    });
  }, [displayData.apps]);

  const appOptions = useMemo(() => {
    return (displayData.apps || []).map((app) => ({ appId: app.appId, name: app.name }));
  }, [displayData.apps]);

  const currentApp = useMemo(() => {
    if (!displayData.apps.length) {
      return undefined;
    }

    if (selectedAppId) {
      const matched = displayData.apps.find((app) => app.appId === selectedAppId);
      if (matched) {
        return matched;
      }
    }

    return displayData.apps[0];
  }, [displayData.apps, selectedAppId]);

  useEffect(() => {
    if (currentIconObjectUrl.current) {
      URL.revokeObjectURL(currentIconObjectUrl.current);
      currentIconObjectUrl.current = null;
    }

    if (!selectedAppId) {
      setCurrentIconUrl(undefined);
      return;
    }

    const selectedApp = reportData?.selectedApps?.find((app) => app.id === selectedAppId);
    const fallbackApp = currentApp;

    const directIconUrl = selectedApp?.iconUrl;
    if (directIconUrl) {
      setCurrentIconUrl(directIconUrl);
      return;
    }

    const iconKey =
      selectedApp?.iconFileKey ??
      reportData?.kintone_apps?.find((app) => app.appId === selectedAppId)?.iconFileKey ??
      fallbackApp?.iconFileKey ??
      null;

    if (!iconKey) {
      setCurrentIconUrl(undefined);
      return;
    }

    if (/^https?:\/\//i.test(iconKey)) {
      setCurrentIconUrl(iconKey);
      return;
    }

    const preset = getKintoneIconUrl(iconKey);
    if (preset) {
      setCurrentIconUrl(preset);
      return;
    }

    const controller = new AbortController();
    let isMounted = true;

    const loadIcon = async () => {
      try {
        const response = await fetch(`/k/v1/file.json?fileKey=${encodeURIComponent(iconKey)}`, {
          method: "GET",
          headers: { "X-Requested-With": "XMLHttpRequest" },
          signal: controller.signal,
        });

        if (!response.ok) {
          return;
        }

        const blob = await response.blob();
        if (!isMounted) {
          return;
        }

        const objectUrl = URL.createObjectURL(blob);
        currentIconObjectUrl.current = objectUrl;
        setCurrentIconUrl(objectUrl);
      } catch (error) {
        if ((error as DOMException)?.name !== "AbortError") {
          console.error("Failed to load kintone icon:", error);
        }
      }
    };

    loadIcon();

    return () => {
      isMounted = false;
      controller.abort();
      if (currentIconObjectUrl.current) {
        URL.revokeObjectURL(currentIconObjectUrl.current);
        currentIconObjectUrl.current = null;
      }
    };
  }, [selectedAppId, reportData?.selectedApps, reportData?.kintone_apps, currentApp]);

  const updateAppData = useCallback((appId: string, field: "purpose", value: string) => {
    setEditableData((prev) => {
      if (!prev) {
        return prev;
      }
      const apps = prev.apps.map((app) => {
        if (app.appId !== appId) {
          return app;
        }
        return {
          ...app,
          [field]: value,
        };
      });
      return { ...prev, apps };
    });
  }, []);

  const currentFunctionalRequirements = currentApp?.functionalRequirements;

  // データがない場合の表示
  if (
    !data ||
    !displayData.apps ||
    displayData.apps.length === 0 ||
    !currentApp ||
    !currentFunctionalRequirements
  ) {
    return (
      <div className={styles["resultContainer"]}>
        <div className={styles["purposeSection"]}>
          <h3 className={styles["purposeTitle"]}>要件分析</h3>
          <p className={styles["purposeText"]}>要件分析データがありません。</p>
        </div>
      </div>
    );
  }

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }));
  };

  // 7分類の要件カテゴリーを配列に変換するヘルパー関数
  const getCategoriesArray = (functionalRequirements: FunctionalRequirements) => {
    const categories = [
      { key: "processManagement", category: functionalRequirements.processManagement },
      { key: "accessRights", category: functionalRequirements.accessRights },
      { key: "notifications", category: functionalRequirements.notifications },
      { key: "actions", category: functionalRequirements.actions },
      { key: "viewFunctions", category: functionalRequirements.viewFunctions },
      { key: "extensions", category: functionalRequirements.extensions },
      { key: "customizations", category: functionalRequirements.customizations },
    ];
    return categories;
  };

  return (
    <div className={styles["resultContainer"]} data-export-root="reverse-engineering">
      {/* System Purpose */}
      {displayData.systemPurpose && (
        <div className={styles["purposeSection"]}>
          <h3 className={styles["purposeTitle"]}>システムの目的</h3>
          {isEditMode && editableData ? (
            <textarea
              className={styles["editableInput"]}
              value={editableData.systemPurpose}
              onChange={(e) =>
                setEditableData({
                  ...editableData,
                  systemPurpose: e.target.value,
                })
              }
              rows={3}
            />
          ) : (
            <p className={styles["purposeText"]}>{displayData.systemPurpose}</p>
          )}
        </div>
      )}

      {/* App Analysis Section */}
      <div className={styles["analysisSection"]}>
        <h3 className={styles["sectionTitle"]}>アプリ別分析</h3>

        {/* App Selector */}
        <div className={styles["appSelector"]}>
          <select
            value={selectedAppId}
            onChange={(e) => setSelectedAppId(e.target.value)}
            className={styles["appDropdown"]}
            disabled={!appOptions.length}
          >
            {appOptions.map((app) => (
              <option key={app.appId} value={app.appId}>
                {app.name} (アプリID: {app.appId})
              </option>
            ))}
          </select>
        </div>

        {/* App Details */}
        <div className={styles["appDetails"]}>
          {/* App Header */}
          <div className={styles["appHeader"]}>
            <div
              className={styles["appIcon"]}
              style={currentIconUrl ? { backgroundImage: `url(${currentIconUrl})` } : undefined}
            />
            <span className={styles["appName"]}>
              {currentApp.name} (アプリID: {currentApp.appId})
            </span>
          </div>

          {/* App Purpose */}
          <div className={styles["appPurpose"]}>
            <h4 className={styles["purposeSubtitle"]}>アプリの目的</h4>
            {isEditMode && editableData ? (
              <textarea
                className={styles["editableInput"]}
                value={currentApp?.purpose || ""}
                onChange={(e) => updateAppData(selectedAppId, "purpose", e.target.value)}
                rows={2}
              />
            ) : (
              <p className={styles["purposeDescription"]}>{currentApp?.purpose}</p>
            )}
          </div>

          {/* App Stats */}
          <div className={styles["appStats"]}>
            <div className={styles["statCard"]}>
              <div className={styles["statLabel"]}>フィールド数</div>
              <div className={styles["statValue"]}>{currentApp?.fieldCount}</div>
            </div>
            <div className={styles["statCard"]}>
              <div className={styles["statLabel"]}>レコード数</div>
              <div className={styles["statValue"]}>{currentApp?.recordCount}</div>
            </div>
          </div>

          {/* Requirements Categories（7分類） */}
          <div className={styles["requirementsContainer"]}>
            {getCategoriesArray(currentFunctionalRequirements).map(({ key, category }) => (
              <div key={key} className={styles["categoryCard"]}>
                <div className={styles["categoryHeader"]} onClick={() => toggleCategory(key)}>
                  <div className={styles["categoryInfo"]}>
                    <span className={styles["categoryName"]}>{category.name}</span>
                    <span className={styles["categoryCount"]}>
                      {category.requirements.length}件
                    </span>
                  </div>
                  <div className={styles["categoryToggle"]}>
                    {expandedCategories[key] ? <ArrowDropDownOpen /> : <ArrowDropDownClose />}
                  </div>
                </div>

                {expandedCategories[key] && category.requirements.length > 0 && (
                  <div className={styles["requirementsList"]}>
                    {category.requirements.map((req: Requirement, reqIndex: number) => (
                      <div key={reqIndex} className={styles["requirementItem"]}>
                        <h4 className={styles["requirementTitle"]}>{req.name}</h4>
                        <p className={styles["requirementDescription"]}>{req.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequirementsAnalysisResult;
